import ApiClient from './ApiClient'
import { isNewRecord } from '../../utils/idHelpers'

class FilterService {
  /**
   * Get the filters of a collect
   * @param {number} collectId - Collect ID
   * @returns {Promise} - Promise resolving the filters
   */
  static getFilters(collectId) {
    return ApiClient.get(`/collects/${collectId}/filters.json`)
      .then(response => {
        const result = {
          userFilterRules: (response.user_filter_rules || []).map(userFilterRule => ({
            ...userFilterRule,
            collectId: userFilterRule.collect_id,
            userId: userFilterRule.user_id,
            rule: userFilterRule.rule ? {
              ...userFilterRule.rule,
              conditions: (userFilterRule.rule.conditions || []).map(condition => ({
                ...condition,
                targetableId: condition.targetable_id,
                targetableType: condition.targetable_type
              }))
            } : userFilterRule.rule
          })),
          variableFilters: (response.variable_filters || []).map(filter => {
            const transformed = {
              id: filter.id,
              variableId: filter.variable_id,
              collectId: filter.collect_id,
              values: filter.values,
              /* eslint-disable-next-line no-underscore-dangle */
              _destroy: filter._destroy
            }
            return transformed
          })
        }
        return result
      })
      .catch(error => {
        throw error
      })
  }

  /**
   * Update the filters of a collect
   * @param {number} collectId - Collect ID
   * @param {Object} filters - Data of the filters to update
   * @returns {Promise} - Promise resolving the response
   */
  static updateFilters(collectId, filters) {
    const formattedData = this.formatFiltersForApi(filters)
    return ApiClient.post(`/collects/${collectId}/filters`, formattedData)
      .then(() => {
        // No log
      })
      .catch(error => {
        throw error
      })
  }

  /**
   * Format the filters data for the API (UserFilterRules format)
   * @private
   * @param {Object} filters - Filters to format
   * @returns {Object} - Formatted data for the API
   */
  static formatFiltersForApi(filters) {
    const { userFilterRules, variableFilters } = filters

    const transformedVariableFilters = variableFilters.map(filter => {
      const transformed = {
        id: isNewRecord(filter.id) ? null : filter.id,
        /* eslint-disable camelcase */
        collect_id: filter.collectId,
        variable_id: filter.variableId,
        /* eslint-enable camelcase */
        values: filter.values,
        /* eslint-disable-next-line no-underscore-dangle */
        _destroy: filter.shouldDestroy || filter._destroy
      }
      return transformed
    })

    // Handle user filter rules
    let userFilterRulesAttributes = []

    if (userFilterRules && userFilterRules.length > 0) {
      const userFilterRule = userFilterRules[0] || {}
      const rule = userFilterRule.rule || { operator: 'every', conditions: [] }

      const ruleAttributes = {
        id: isNewRecord(rule.id) ? null : rule.id,
        operator: rule.operator || 'every',
        /* eslint-disable camelcase */
        conditions_attributes: rule.conditions.map(condition => ({
          id: isNewRecord(condition.id) ? null : condition.id,
          targetable_id: parseInt(condition.targetableId, 10),
          targetable_type: condition.targetableType,
          state: condition.state,
          value: condition.value,
          _destroy: condition.shouldDestroy
        })),
        _destroy: rule.shouldDestroy
        /* eslint-enable camelcase */
      }

      const userFilterRuleAttributes = {
        /* eslint-disable camelcase */
        rule_attributes: ruleAttributes
        /* eslint-enable camelcase */
      }

      userFilterRuleAttributes.id = isNewRecord(userFilterRule.id) ? null : userFilterRule.id
      /* eslint-disable camelcase */
      userFilterRuleAttributes.collect_id = userFilterRule.collectId
      userFilterRuleAttributes.user_id = userFilterRule.userId
      /* eslint-disable-next-line no-underscore-dangle */
      userFilterRuleAttributes._destroy = userFilterRule.shouldDestroy
      /* eslint-enable camelcase */

      userFilterRulesAttributes = [userFilterRuleAttributes]
    }

    const result = {
      /* eslint-disable camelcase */
      user_filter_rules_attributes: userFilterRulesAttributes,
      filters_attributes: transformedVariableFilters
      /* eslint-enable camelcase */
    }

    return result
  }

  /**
   * Get the values of a variable
   * @param {number} collectId - Collect ID
   * @param {number} variableId - Variable ID
   * @returns {Promise} - Promise resolving the variable values
   */
  static getVariableValues(collectId, variableId) {
    return ApiClient.get(`/collects/${collectId}/variables/${variableId}`)
  }
}

export default FilterService